// Employee Profile — "Onboarding Checklist" card matching navigator_people_v83.html
import React, { useState } from "react";
import { View, Text, StyleSheet, Pressable } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { AdminColors } from "../../../Admin/constants/adminColors";
import FontFamily from "../../../../constants/fonts";
import { MyTeamMember } from "../../types";
import { formatShortDate } from "../../formatDate";
import { EmployeeOnboardingPlan, OnboardingChecklistRow } from "../types";
import ProfileDataTable, { ProfileTableColumn } from "./ProfileDataTable";
import CompleteOnboardingModal from "./CompleteOnboardingModal";
import ViewOnboardingRecordModal from "./ViewOnboardingRecordModal";
import EditOnboardingPlanModal from "./EditOnboardingPlanModal";

// Mockup tokens not present in AdminColors
const NAV_BLUE_100 = "#C6DCFB";
const NAV_RED_50 = "#FBE6E9";
const NAV_GREEN_500 = "#008553";
const NAV_GREEN_TEXT = "#0C420B";

interface OnboardingChecklistCardProps {
  plan: EmployeeOnboardingPlan;
  member: MyTeamMember;
  onEditPlan?: () => void;
  onRefreshProfile?: () => void;
}

const CheckpointPoint: React.FC<{ card: EmployeeOnboardingPlan["phases"][number] }> = ({ card }) => {
  const isDone = card.state === "done" || (card.completed > 0 && card.completed === card.total);
  const isCurrent = card.state === "current" || card.current;
  const isLate = card.state === "late" || card.overdue;

  return (
    <View
      style={[
        styles.ptcPoint,
        isDone && styles.ptcPointDone,
        isCurrent && styles.ptcPointCurrent,
        isLate && styles.ptcPointLate,
      ]}
    >
      <View
        style={[
          styles.ptcPointDot,
          isDone && styles.ptcPointDotDone,
          isCurrent && styles.ptcPointDotCurrent,
          isLate && styles.ptcPointDotLate,
        ]}
      />
      <View style={styles.ptcPointTextRow}>
        <Text
          style={[
            styles.ptcPointLabel,
            isDone && styles.ptcPointLabelDone,
            isCurrent && styles.ptcPointLabelCurrent,
            isLate && styles.ptcPointLabelLate,
          ]}
          numberOfLines={1}
        >
          {card.key}
        </Text>
        <Text
          style={[
            styles.ptcPointCount,
            isDone && styles.ptcPointCountDone,
            isCurrent && styles.ptcPointCountCurrent,
            isLate && styles.ptcPointCountLate,
          ]}
        >
          {card.completed}/{card.total}
        </Text>
      </View>
    </View>
  );
};

const PriorityBadge: React.FC<{ priority: OnboardingChecklistRow["priority"] }> = ({ priority }) => {
  const isMandatory = priority === "Mandatory";
  return (
    <View style={[styles.pill, { backgroundColor: isMandatory ? AdminColors.red75 : AdminColors.gray50 }]}>
      <Text style={[styles.pillText, { color: isMandatory ? AdminColors.red700 : AdminColors.gray600 }]}>
        {priority}
      </Text>
    </View>
  );
};

const StatusBadge: React.FC<{ status: OnboardingChecklistRow["status"] }> = ({ status }) => {
  const isDone = status === "Done";
  const isInProgress = status === "In Progress";
  const tone = isDone
    ? { bg: AdminColors.green75, fg: NAV_GREEN_TEXT }
    : isInProgress
    ? { bg: AdminColors.tagOrange.bg, fg: AdminColors.tagOrange.text }
    : { bg: AdminColors.gray50, fg: AdminColors.gray600 };

  return (
    <View style={[styles.pill, { backgroundColor: tone.bg }]}>
      <Text style={[styles.pillText, { color: tone.fg }]}>{status}</Text>
    </View>
  );
};

const OnboardingChecklistCard: React.FC<OnboardingChecklistCardProps> = ({
  plan,
  member,
  onEditPlan,
  onRefreshProfile,
}) => {
  const [selectedStartItem, setSelectedStartItem] = useState<OnboardingChecklistRow | null>(null);
  const [selectedViewItem, setSelectedViewItem] = useState<OnboardingChecklistRow | null>(null);
  const [isEditPlanOpen, setIsEditPlanOpen] = useState(false);

  const hasPlan = plan.phases.length > 0 || plan.rows.length > 0;
  const isPlanActive = (plan.status || "Active").toLowerCase() === "active";
  const openCount = plan.openCount ?? (plan.totalCount - plan.completedCount);

  const columns: ProfileTableColumn<OnboardingChecklistRow>[] = [
    { key: "phase", label: "Phase", flex: 0.9 },
    {
      key: "activity",
      label: "Activity",
      flex: 2.2,
      render: (row) => (
        <View style={styles.activityCol}>
          <Text style={styles.activityText}>{row.activity}</Text>
          {(row.completionType || row.completedOn) && (
            <Text style={styles.activityMeta}>
              {row.completionType || row.type}
              {row.completedOn ? ` | Completed ${formatShortDate(row.completedOn)} by ${row.completedBy || "Supervisor"}` : ""}
            </Text>
          )}
        </View>
      ),
    },
    { key: "type", label: "Type", flex: 0.9 },
    {
      key: "priority",
      label: "Priority",
      flex: 0.9,
      render: (row) => <PriorityBadge priority={row.priority} />,
    },
    {
      key: "due",
      label: "Due",
      flex: 1,
      getCellStyle: (row) =>
        row.status !== "Done" && row.isOverdue ? { backgroundColor: NAV_RED_50 } : undefined,
      render: (row) => (
        <Text style={[styles.dueText, row.status !== "Done" && row.isOverdue && styles.metricBad]}>
          {formatShortDate(row.due)}
        </Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      flex: 0.9,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "action",
      label: "",
      flex: 1.1,
      render: (row) => {
        if (row.status === "Done") {
          return row.completionRecord ? (
            <Pressable style={styles.viewRecordButton} onPress={() => setSelectedViewItem(row)}>
              <Text style={styles.viewRecordButtonText}>View record</Text>
            </Pressable>
          ) : (
            <Text style={styles.importedText}>Imported</Text>
          );
        }

        return (
          <Pressable style={styles.startButton} onPress={() => setSelectedStartItem(row)}>
            <Text style={styles.startButtonText}>{row.status === "In Progress" ? "Resume" : "Start"}</Text>
          </Pressable>
        );
      },
    },
  ];

  return (
    <View style={styles.card} nativeID="employee-onboarding-plan">
      {/* Header */}
      <View style={styles.headerRow}>
        <Text style={styles.title}>Onboarding Checklist</Text>
        {hasPlan && (
          <View style={styles.headerRight}>
            <View
              style={[
                styles.pill,
                {
                  backgroundColor: isPlanActive ? AdminColors.blue75 : AdminColors.green75,
                },
              ]}
            >
              <Text
                style={[
                  styles.pillText,
                  { color: isPlanActive ? AdminColors.blue800 : NAV_GREEN_TEXT },
                ]}
              >
                {plan.status || "Active"}
              </Text>
            </View>

            <View
              style={[
                styles.pill,
                {
                  backgroundColor: plan.overdueCount > 0 ? AdminColors.red75 : AdminColors.gray50,
                },
              ]}
            >
              <Text
                style={[
                  styles.pillText,
                  { color: plan.overdueCount > 0 ? AdminColors.red700 : AdminColors.gray600 },
                ]}
              >
                {plan.overdueCount > 0 ? `${plan.overdueCount} overdue` : `${openCount} open`}
              </Text>
            </View>

            <Pressable style={styles.editPlanButton} onPress={() => (onEditPlan ? onEditPlan() : setIsEditPlanOpen(true))}>
              <Text style={styles.editPlanButtonText}>Edit plan</Text>
            </Pressable>
          </View>
        )}
      </View>

      {!hasPlan ? (
        <View style={styles.emptyState}>
          <View style={styles.emptyIconCircle}>
            <MaterialIcons name="playlist-add-check" size={24} color={AdminColors.blue700} />
          </View>
          <Text style={styles.emptyTitle}>No onboarding plan</Text>
          <Text style={styles.emptySubtitle}>This employee does not have an onboarding checklist.</Text>
        </View>
      ) : (
        <>
          {/* Day & Completed summary + checkpoint strip */}
          <View style={styles.summaryBox}>
            <View style={styles.summaryHead}>
              <Text style={styles.summaryDayText}>{plan.dayLabel}</Text>
              <Text style={styles.summaryCompleteText}>
                {plan.completedCount} of {plan.totalCount} complete
              </Text>
            </View>
            <View style={styles.ptcTrack}>
              {plan.phases.map((p) => (
                <CheckpointPoint key={p.key} card={p} />
              ))}
            </View>
          </View>

          {/* Data Table */}
          <ProfileDataTable
            variant="plan"
            columns={columns}
            rows={plan.rows}
            searchFields={["activity", "phase", "type"]}
            emptyLabel="No onboarding items found."
          />
        </>
      )}

      {/* Start / Complete Activity Modal */}
      {selectedStartItem && (
        <CompleteOnboardingModal
          visible={!!selectedStartItem}
          onClose={() => setSelectedStartItem(null)}
          item={selectedStartItem}
          member={member}
          managerName={member.manager || "Supervisor"}
          onCompleteSuccess={() => {
            onRefreshProfile?.();
          }}
        />
      )}

      {/* Edit Plan Modal */}
      {isEditPlanOpen && (
        <EditOnboardingPlanModal
          visible={isEditPlanOpen}
          onClose={() => setIsEditPlanOpen(false)}
          plan={plan}
          member={member}
          onSaveSuccess={() => onRefreshProfile?.()}
        />
      )}

      {/* View Record Modal */}
      {selectedViewItem && (
        <ViewOnboardingRecordModal
          visible={!!selectedViewItem}
          onClose={() => setSelectedViewItem(null)}
          item={selectedViewItem}
          member={member}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    padding: 24,
    gap: 16,
    // --nav-elevation-01: 0 0 24px #005ec014
    shadowColor: "#005EC0",
    shadowOpacity: 0.08,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 2,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontSize: 20,
    lineHeight: 28,
    color: AdminColors.textPrimary,
  },
  headerRight: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
  },
  summaryBox: {
    borderWidth: 1,
    borderColor: NAV_BLUE_100,
    backgroundColor: AdminColors.blue25,
    borderRadius: 8,
    padding: 14,
    gap: 12,
  },
  summaryHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: -4,
  },
  summaryDayText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: AdminColors.textSecondary,
  },
  summaryCompleteText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    lineHeight: 18,
    color: AdminColors.textPrimary,
  },
  ptcTrack: {
    flexDirection: "row",
    gap: 4,
  },
  ptcPoint: {
    flex: 1,
    minWidth: 0,
    backgroundColor: AdminColors.gray25,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 2,
    alignItems: "center",
    gap: 3,
  },
  ptcPointDone: {
    backgroundColor: AdminColors.green75,
  },
  ptcPointCurrent: {
    backgroundColor: AdminColors.blue50,
    // inset 1.5px ring: keep layout identical to other tiles
    borderWidth: 1.5,
    borderColor: AdminColors.blue600,
    paddingVertical: 4.5,
    paddingHorizontal: 0.5,
  },
  ptcPointLate: {
    backgroundColor: AdminColors.red75,
  },
  ptcPointDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: AdminColors.gray100,
  },
  ptcPointDotDone: {
    backgroundColor: NAV_GREEN_500,
  },
  ptcPointDotCurrent: {
    backgroundColor: AdminColors.blue700,
  },
  ptcPointDotLate: {
    backgroundColor: AdminColors.red500,
  },
  // Phase label and its completed/total count share one row; wraps only if the tile is too narrow.
  ptcPointTextRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    columnGap: 6,
  },
  ptcPointLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 10,
    lineHeight: 14,
    color: AdminColors.textPrimary,
    textAlign: "center",
  },
  ptcPointLabelDone: {
    color: NAV_GREEN_TEXT,
  },
  ptcPointLabelCurrent: {
    color: AdminColors.blue900,
  },
  ptcPointLabelLate: {
    color: AdminColors.red700,
  },
  ptcPointCount: {
    fontFamily: FontFamily.regular,
    fontSize: 10,
    lineHeight: 14,
    color: AdminColors.textSecondary,
    textAlign: "center",
  },
  ptcPointCountDone: {
    color: NAV_GREEN_TEXT,
  },
  ptcPointCountCurrent: {
    color: AdminColors.blue900,
  },
  ptcPointCountLate: {
    color: AdminColors.red700,
  },
  // .btn.btn-secondary.btn-sm
  editPlanButton: {
    height: 32,
    borderWidth: 1,
    borderColor: AdminColors.blue800,
    borderRadius: 24,
    paddingHorizontal: 16,
    backgroundColor: AdminColors.bgSurface,
    alignItems: "center",
    justifyContent: "center",
  },
  editPlanButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.blue800,
  },
  activityCol: {
    gap: 2,
  },
  activityText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.textPrimary,
  },
  activityMeta: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: AdminColors.textSecondary,
  },
  dueText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.textPrimary,
  },
  metricBad: {
    color: AdminColors.red500,
    fontFamily: FontFamily.bold,
  },
  // .tag
  pill: {
    height: 24,
    borderRadius: 4,
    paddingHorizontal: 8,
    alignSelf: "flex-start",
    justifyContent: "center",
  },
  pillText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    lineHeight: 18,
  },
  importedText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: AdminColors.textSecondary,
  },
  // .btn.btn-primary.btn-sm
  startButton: {
    height: 32,
    backgroundColor: AdminColors.blue800,
    borderRadius: 24,
    paddingHorizontal: 16,
    alignSelf: "flex-start",
    alignItems: "center",
    justifyContent: "center",
  },
  startButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: "#FFFFFF",
  },
  // .btn.btn-secondary.btn-sm
  viewRecordButton: {
    height: 32,
    borderWidth: 1,
    borderColor: AdminColors.blue800,
    borderRadius: 24,
    paddingHorizontal: 16,
    alignSelf: "flex-start",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: AdminColors.bgSurface,
  },
  viewRecordButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.blue800,
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 32,
    gap: 6,
  },
  emptyIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: AdminColors.blue50,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 4,
  },
  emptyTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    color: AdminColors.textPrimary,
  },
  emptySubtitle: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textSecondary,
  },
});

export default OnboardingChecklistCard;
